<template>
  <div class="sc__systems-tabs">
    <Product
      v-if="displayProductHeader"
      :product-name="productDetails.displayName"
      :product-image="productDetails.imageURL"
    />

    <div
      v-if="displayProductFamilyHeader"
      class="sc__systems-tabs__info-wrapper"
    >
      <h2 class="sc__systems-tabs__product-family__name">
        <!-- eslint-disable vue/no-v-html -->
        <a
          href="#"
          v-html="getInitialSelectedProductFamilyName"
        />
        <!-- eslint-enable vue/no-v-html -->
      </h2>
    </div>
    <ScTabs
      :tabs="contentTabs"
      :initial-tab="$route.params.tabId"
      @on-tab-selected="onTabSelected"
    >
      <!-- Tab Topics -->
      <template #tab-topics>
        {{ translations.systemsSupportCenterTabs.topicsTab }}
      </template>
      <template #tab-panel-topics>
        <div class="sc__systems-tabs__topics-panel">
          <ContentSearchable
            :expand-filter="isSearchFiltersExpanded || false"
          />
          <TheContentResults />
        </div>
      </template>

      <!-- Tab Manuals -->
      <template #tab-manuals>
        {{ translations.systemsSupportCenterTabs.manualsTab.label }}
      </template>
      <template #tab-panel-manuals>
        <div class="sc__systems-tabs__manuals-panel">
          <!-- eslint-disable max-len -->
          <ScContentManuals
            :is-loading="isLoading"
            :no-manuals-in-selected-language="translations.systemsSupportCenterTabs.manualsTab.noManualsInTheSelectedLanguage"
            :heading="translations.systemsSupportCenterTabs.manualsTab.label"
            :no-results-copy="translations.systemsSupportCenterTabs.manualsTab.noManualsAvailable"
            :versions-label="translations.systemsSupportCenterTabs.manualsTab.versionSelectorLabel"
            :versions-default-label="translations.systemsSupportCenterTabs.manualsTab.versionSelectorPrompt"
            :show-all-publications="manualsDisplayAllPub"
            :active-versions="hasActiveVersions"
            :manuals="contentManuals"
            :international-manuals="contentInternationalManuals"
            :view-all-publications-link-text="translations.systemsSupportCenterTabs.manualsTab.viewAllManuals"
            :table-heading-manual-name="translations.systemsSupportCenterTabs.manualsTab.manualName"
            :table-heading-manual-lang="translations.systemsSupportCenterTabs.manualsTab.language"
            :ga-event-string-selected="`${systemConfig.mode} - Manuals,Link,[VALUE]`"
            :ga-event-string-view-all="`${systemConfig.mode} - Manuals,Link,View publications in all languages`"
            @on-manual-link-clicked="onManualLinkClicked"
            @on-view-all-publications-clicked="manualsDisplayAllPub = !manualsDisplayAllPub"
            @on-version-selected="onManualsVersionSelected"
          >
            <template #manual="{ manual }">
              <ScContentManual
                :revision-heading="translations.systemsSupportCenterTabs.manualsTab.revisionLabel"
                v-bind="manual"
                :revision-date="manual.revisionDate ? getFriendlyManualRevDate(manual.revisionDate) : null"
                :ga-event-string-selected="`${systemConfig.mode} - Manuals,Link,${manual.pumacFullURL}`"
                @on-manual-link-clicked="onManualLinkClicked"
              />
            </template>
          </ScContentManuals>
          <!-- eslint-enable max-len -->
        </div>
      </template>

      <!-- Tab Software -->
      <template #tab-software>
        {{ translations.systemsSupportCenterTabs.softwareTab }}
      </template>
      <template #tab-panel-software>
        <div class="sc__systems-tabs__software-panel">
          <ContentFaqRenderable
            v-if="faqDetails"
            :faq-details="faqDetails"
            @on-faq-clicked="onSoftwareFaqClicked"
          >
            <template #backLink>
              <ScContentBackLink
                v-if="shouldRenderSoftwareBackLink"
                class="sc__systems--back"
                :text="translations.commonSettings.navigation.back"
                :prev-url="getComputedBackLinkUrl"
                :ga-event-string="`Return To Search,Link,${systemConfig.mode} - Search`"
                @on-back-link-clicked="onSoftwareBackLinkClicked"
              />
            </template>
          </ContentFaqRenderable>
          <div
            v-if="translations.systemsSupportCenterTabs.securityEnabled"
          >
            <h3>
              {{ translations.systemsSupportCenterTabs.securityTitle }}
            </h3>
            <span v-html="translations.systemsSupportCenterTabs.securityText" />
            <span v-if="getEndDate">
              {{ translations.systemsSupportCenterTabs.securitySecondSentence + getEndDate + "." }}
            </span>
          </div>
        </div>
      </template>

      <!-- Tab Videos -->
      <template #tab-videos>
        {{ translations.systemsSupportCenterTabs.videosTab.label }}
      </template>
      <template #tab-panel-videos>
        <div class="sc__systems-tabs__videos">
          <div v-if="hasResults(contentVideoResults)">
            <ScContentVideoResults
              :videos="getVideos"
              :heading="translations.systemsSupportCenterTabs.videosTab.label"
            />

            <g-button
              v-if="renderShowMoreVideosButton"
              theme="light"
              size="medium"
              type="submit"
              @click="onShowMoreVideosClick"
            >
              {{ translations.commonSettings.navigation.showMore }}
            </g-button>
            <div
              v-if="renderVideosDisclaimer"
              class="sc__systems-tabs__videos--disclaimer"
              v-html="renderVideosDisclaimer"
            />
          </div>
          <div v-else-if="!isLoading">
            <h3 class="section-heading">
              {{ translations.systemsSupportCenterTabs.videosTab.label }}
            </h3>
            <p>
              {{ translations.systemsSupportCenterTabs.videosTab.noVideosAvailableMessage }}
            </p>
          </div>
        </div>
      </template>

      <!-- Tab AviationOTC -->
      <template #tab-aviationOtc>
        {{ translations.systemsSupportCenterTabs.aviationOtcTab }}
      </template>
      <template #tab-panel-aviationOtc>
        <div class="sc__systems-tabs__aviationOtc">
          <!-- eslint-disable max-len -->
          <div>
            <ScAviationOTCTab
              v-if="isAuthenticated"
              :aviation-documents="aviationDocuments"
              :revision-heading="translations.systemsSupportCenterTabs.aviationTab.revisionHeading"
              :heading="translations.systemsSupportCenterTabs.aviationTab.manualsHeading"
              :version-label="translations.systemsSupportCenterTabs.aviationTab.versionLabel"
              :no-results="translations.systemsSupportCenterTabs.aviationTab.noResultsLabel"
              :no-manuals="translations.systemsSupportCenterTabs.aviationTab.noManuals"
              :compliance-heading="translations.systemsSupportCenterTabs.aviationTab.compliance"
              :doc-heading="translations.systemsSupportCenterTabs.aviationTab.docId"
              :versions-default-label="translations.systemsSupportCenterTabs.aviationTab.version"
              :available-documents="translations.systemsSupportCenterTabs.aviationTab.availableDocs"
              :not-reg-heading="translations.systemsSupportCenterTabs.aviationTab.notRegHeading"
              :not-reg-content="translations.systemsSupportCenterTabs.aviationTab.notRegContent"
              :not-reg-link-text="translations.systemsSupportCenterTabs.aviationTab.notRegLinkText"
              :not-reg-link="translations.systemsSupportCenterTabs.aviationTab.notRegLink"
              :content-summary-translations="contentSummaryTranslations"
              :keyword-label="translations.systemsSupportCenterTabs.aviationTab.keywordSearchLabel"
              :no-results-messaging="translations.systemsSupportCenterTabs.aviationTab.noResultsMessaging"
              :keyword-search-attr="{ placeholder: translations.systemsSupportCenterTabs.aviationTab.keywordSearchPlaceholder }"
              @fetch-aviation-docs="fetchAviationDocs"
            />
            <!-- eslint-enable max-len -->
            <div
              v-else
              class="aviationOTC--logged-out"
            >
              <h2 v-if="translations.systemsSupportCenterTabs.loggedOutHeading">
                {{ translations.systemsSupportCenterTabs.loggedOutHeading }}
              </h2>
              <p>
                {{ translations.systemsSupportCenterTabs.loginMessaging }}
              </p>
              <a
                v-if="translations.systemsSupportCenterTabs.loginLinkText"
                :href="getAccountLoginUrl()"
                target="_self"
                @click.prevent="onHelpLinkClick({
                  event_category: 'Aviation OTC', event_action : 'Link',
                  event_label: 'Login for OTC', link: getAccountLoginUrl()
                })"
              >
                {{ translations.systemsSupportCenterTabs.loginLinkText }}
              </a>
            </div>
          </div>
        </div>
      </template>
    </ScTabs>
  </div>
</template>

<script src="./index.js"></script>

<style scoped lang="scss" src="./style.scss"></style>
